import { createTheme } from '@mui/material/styles';

const getThemeColors = () => {
  // Try to get the colors from localStorage (will be set by App.jsx when token is processed)
  const themeColorsString = localStorage.getItem('themeColors');
  
  // Default fallback colors if themeColors not available
  const defaultLight = '#EAFFFB';
  const defaultMain = '#1DCEAB';
  
  if (themeColorsString) {
    try {
      const commaIndex = themeColorsString.indexOf(',');
      if (commaIndex !== -1) {
        const light = themeColorsString.substring(0, commaIndex);
        const main = themeColorsString.substring(commaIndex + 1);
        return { light, main };
      }
    } catch (error) {
      console.error('Error parsing theme colors:', error);
    }
  }
  
  // Return defaults if any issues occur or themeColors not set
  return { light: defaultLight, main: defaultMain };
};

// Generate the theme with current colors
export const createAppTheme = (customColors = null) => {
  const themeColors = customColors || getThemeColors();
  const secondaryColorLight = themeColors.light;
  const secondaryColorMain = themeColors.main;

  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 1024,
        lg: 1200,
        xl: 1536,
        xxl: 1740,
      },
    },

    typography: {
      fontFamily: [
        'Geist',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Arial',
        'sans-serif',
      ].join(','),
      button: {
        textTransform: 'none',
      },
      h2: {
        fontSize: 24,
        fontWeight: 600,
        lineHeight: 1.4,
      },
    },

    palette: {
      primary: {
        light: '#71717A',
        main: '#18181B',
        dark: '#000000',
        contrastText: '#ffffff',
      },
      secondary: {
        light: secondaryColorLight,
        main: secondaryColorMain,
        contrastText: '#ffffff',
      },
      error: {
        main: '#f44336',
      },
      background: {
        default: '#fff',
      },
    },

    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            border: 'none',
            '&:hover': {
              backgroundColor: 'none',
            },
          },
        },
        variants: [
          {
            props: {
              variant: 'buttonStandard',
            },
            style: {
              backgroundColor: '#18181B',
              color: '#ffffff',
              fontSize: 12,
              borderRadius: 6,
              padding: '8px 16px',
              '&:hover': {
                backgroundColor: '',
              },
              '&:disabled': {
                backgroundColor: '#ECECEC'
              }
            },
          },
          {
            props: {
              variant: 'buttonHighlight',
            },
            style: {
              backgroundColor: secondaryColorMain,
              color: '#ffffff',
              fontSize: 16,
              borderRadius: 6,
              '&:hover': {
                color: '#ffffff',
                backgroundColor: '#18181B'
              },
              '&:disabled': {
                color: '#D0D0D0',
                backgroundColor: '#F4F4F5',
              }
            },
          },
          {
            props: {
              variant: 'cancelButton',
            },
            style: {
              color: '#71717A',
              border: '1px solid #CBCED5',
              fontSize: 12,
              '&:hover': {
                color: '#ffffff',
                backgroundColor: '#18181B',
                boxShadow: 'none',
                border: '1px solid transparent',
              },
              '&:disabled': {
                color: '#D0D0D0',
                border: '1px solid #E4E4E7',
              },
            },
          },

          {
            props: {
              variant: 'outlined',
            },
            style: {
              border: '1px solid transparent',
              '&:disabled': {
                border: '1px solid transparent',
              },
            },
          },
          {
            props: {
              variant: 'saveButton',
            },
            style: {
              backgroundColor: secondaryColorMain,
              color: '#ffffff',
              fontSize: 12,
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: '#18181B',
              },
              '&:disabled': {
                color: '#D0D0D0',
                backgroundColor: '#F4F4F5',
              },
            },
          },
          {
            props: {
              variant: 'badgePrimary',
            },
            style: {
              backgroundColor: '#18181B',
              color: '#FAFAFA',
              borderRadius: '9999px',
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              minWidth: 'auto',
              boxShadow: 'none',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: '#2D2D35',
              },
              '&:disabled': {
                color: '#ffffff',
                backgroundColor: '#8C8C95',
              }
            }
          },
          {
            props: { 
              variant: 'badgeSecondary' 
            },
            style: {
              backgroundColor: '#ffffff',
              color: '#8C8C95',
              borderRadius: '9999px',
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 500,
              textTransform: 'uppercase',
              minWidth: 'auto',
              border: '1px solid #D7D7D7',
              boxShadow: 'none',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: '#E4E4E7',
                border: '1px solid #D7D7D7',
              }
            }
          },
        ],
      },
      MuiListItem: {
        variants: [
          {
            props: { 
              variant: 'menuItem' 
            },
            style: {
              cursor: 'pointer',
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(29, 206, 171, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(29, 206, 171, 0.2)',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }
          },
        ]
      },
      MuiListItemText: {
        variants: [
          {
            props: { 
              variant: 'menuItem' 
            },
            style: {
              '& .MuiTypography-root': {
                fontWeight: 500,
                fontSize: 16,
                color: '#09090B'
              }
            }
          },
        ]
      },
      MuiListItemIcon: {
        variants: [
          {
            props: { 
              variant: 'menuItem' 
            },
            style: {
              minWidth: 40,
              color: '#09090B',
            }
          },
        ]
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
            borderRadius: '8px',
            border: '1px solid #E4E4E7',
          },
        },
      },
      
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 14,
            padding: '8px 16px',
            '&:hover': {
              backgroundColor: '#F4F4F5',
            },
            '&.Mui-selected': {
              backgroundColor: '#F4F4F5',
              '&:hover': {
                backgroundColor: '#E4E4E7',
              },
            },
          },
        },
      },
      
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: 14,
            padding: '8px 12px',
          },
        },
      },
    },
  });
};

// Export a default theme instance (for static imports)
export const theme = createAppTheme(); 